/**
 * GP-1347 — the Nursing Home chip every queue's delivery-type ("Type") column renders.
 *
 * GP-1314 gave NH orders their own chip so they stopped reading "Not Set", but the
 * chip said only "Nursing Home" and the destination lived in the tooltip. Staff scan
 * these queues; a hover is not a scan. Dr. Jog's patients are labelled "nursing home"
 * too, so without the home ON the chip a Chatham-Kent row is indistinguishable from a
 * Wallaceburg one or from a Jog patient. The label now carries both: the mode AND the
 * home ("Nursing Home – Chatham-Kent").
 *
 * ONE component rather than the three near-identical copies this replaced
 * (orderQueueColumns ×2, readyToShipColumns ×1) — the same reason the NH filter is a
 * single component: a divergent copy is how PHARMACY_INFO drifted.
 *
 * NO FALLBACK: an order with no Kroll group stamp renders the bare mode. A guessed or
 * placeholder home name on a fulfilment worksheet routes medication to the wrong
 * building — see `getNursingHomeGroupName`.
 */
import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import { HomeWork } from '@mui/icons-material';
import {
  getNursingHomeGroupName,
  type NursingHomeOrderRowLike,
} from '../../utils/nursingHomeOrder';

/**
 * "Nursing Home – <home>", or the bare mode when the order carries no stamp.
 * Exported so queues that render their own chip stay on one wording.
 */
export const getNursingHomeChipLabel = (
  row: NursingHomeOrderRowLike | null | undefined,
): string => {
  const homeName = getNursingHomeGroupName(row);
  return homeName ? `Nursing Home – ${homeName}` : 'Nursing Home';
};

/** Why the row shows no courier delivery type, naming the home when we have it. */
export const getNursingHomeChipTooltip = (
  row: NursingHomeOrderRowLike | null | undefined,
): string => {
  const homeName = getNursingHomeGroupName(row);
  return homeName
    ? `Nursing home drop-off: ${homeName} — no courier delivery type applies`
    : 'Nursing home drop-off — no courier delivery type applies';
};

interface NursingHomeDeliveryTypeChipProps {
  row: NursingHomeOrderRowLike | null | undefined;
  /** Compact-grid sizing (22px row chips). Ready to Ship uses the roomier default. */
  dense?: boolean;
  /** Cap before the label ellipsises; the full text stays in the tooltip. */
  maxWidth?: number;
}

export const NursingHomeDeliveryTypeChip: React.FC<NursingHomeDeliveryTypeChipProps> = ({
  row,
  dense = true,
  maxWidth,
}) => (
  <Tooltip title={getNursingHomeChipTooltip(row)}>
    <Chip
      icon={<HomeWork sx={{ fontSize: dense ? '0.8rem' : '0.9rem' }} />}
      label={getNursingHomeChipLabel(row)}
      color="secondary"
      variant="outlined"
      size="small"
      sx={{
        cursor: 'help',
        maxWidth: maxWidth ?? (dense ? 160 : 190),
        ...(dense ? { height: 22, fontSize: '0.7rem', fontWeight: 'medium' } : {}),
        '& .MuiChip-label': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      }}
    />
  </Tooltip>
);

export default NursingHomeDeliveryTypeChip;
